import { Button, Flex } from '@brightdrop/bd-ui';
import { Typography } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';

import ChevronDownIcon from '~/assets/icons/new/chevron-down.svg?react';
import TranslatedLabel from '~/common/components/TranslatedLabel/TranslatedLabel';
import { HEADER_HEIGHT } from '~/features/header/GlobalHeader.styles';

import {
  type HeaderInfo,
  useStickyHeaders,
} from '../../hooks/StickyHeaderContext';

/**
 * Props for the StickyHeader component
 */
interface NotificationHeaderProps {
  /** Unique identifier for the header */
  id: string;
  /** Display text for the header */
  label: string;
  /** Callback function to be called when the showLess button is clicked */
  showLessCallback?: () => void;
  /** Type of header */
  type: 'section' | 'group';
  /** Whether the header is in the pinned section */
  inPinnedSection?: boolean;
  /** Whether the show less button in the pinned section should be visible */
  showPinnedSectionButton?: boolean;
}

/**
 * NotificationHeader Component
 * A component that renders a header above a notification list and manages its visibility state based on scroll position.
 * When the header scrolls above the viewport (past the global header), it registers itself as "above viewport"
 * which allows the StickyNotificationHeader to display a sticky version at the top of the page.
 *
 * @param props - Component props
 * @returns JSX element representing the section header
 */
const NotificationHeader = ({
  id,
  label,
  type,
  showLessCallback,
  inPinnedSection,
  showPinnedSectionButton,
}: NotificationHeaderProps) => {
  const { registerHeader, unregisterHeader, updateHeaderVisibility } =
    useStickyHeaders();
  const elementRef = useRef<HTMLDivElement>(null);

  /**
   * Gets the current vertical position of the header element
   */
  const getVerticalPosition = useCallback(() => {
    if (!elementRef.current) return 0;
    return elementRef.current.getBoundingClientRect().top + window.pageYOffset;
  }, []);

  /**
   * Handles scroll events to update header visibility state
   * Determines if the header has scrolled above the viewport threshold
   */
  const handleScroll = useCallback(() => {
    if (!elementRef.current) return;

    const rect = elementRef.current.getBoundingClientRect();
    /* Sticky Header is above viewport when its top edge is completely above the global header */
    const isAboveViewport =
      type === 'section'
        ? rect.top <= HEADER_HEIGHT
        : rect.top <= HEADER_HEIGHT + 32;

    updateHeaderVisibility(id, isAboveViewport);
  }, [id, type, updateHeaderVisibility]);

  useEffect(() => {
    const headerInfo: HeaderInfo = {
      id,
      label,
      verticalPosition: getVerticalPosition(),
      type,
      showLessCallback,
    };
    registerHeader(headerInfo);

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      unregisterHeader(id);
    };
  }, [
    id,
    label,
    type,
    registerHeader,
    unregisterHeader,
    handleScroll,
    showLessCallback,
    getVerticalPosition,
  ]);

  return (
    <div ref={elementRef} data-testid={id}>
      <Flex
        style={{
          paddingBlock: label ? (type === 'section' ? '14px' : '6px') : '0px',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography
          variant={type === 'section' ? 'body_medium' : 'small_body_medium'}
        >
          {label}
        </Typography>

        {(type === 'group' || (inPinnedSection && showPinnedSectionButton)) &&
          label &&
          showLessCallback && (
            <Button
              variant="button-link"
              onClick={showLessCallback}
              endIcon={
                <ChevronDownIcon style={{ transform: 'rotate(180deg)' }} />
              }
              iconColorRule="stroke"
            >
              <TranslatedLabel
                translationKey="common:showLess"
                typographyVariant="label_medium"
              />
            </Button>
          )}
      </Flex>
    </div>
  );
};

export default NotificationHeader;
